import { Classes, SectionCard } from "@blueprintjs/core";
import { useRegionConfig } from "in_queue/contexts/ClusterContext";
import { useIncludesCrossClusterQueueProjects } from "in_queue/contexts/ProjectDataContext";
import {
    getCycleAndGroupLabel,
    LegacyClusterInfo
} from "in_queue/types/clusterType";
import {
    ProjectType,
    QueueProject,
    SpeculativeProject
} from "in_queue/types/projectType";
import { ScenarioMetadata } from "in_queue/types/scenarioType";
import { useMemo } from "react";
import { ScenarioAssumptionsSection } from "../ScenarioAssumptions";
import css from "../ScenarioAssumptions.module.scss";
import { ExportAssumptionsButton } from "./ExportAssumptionsButton";
import { ProjectSizePreviewWrapper } from "./ProjectSizePreviewWrapper";
import { getSizeAdjustments } from "./sizeAdjustments";

export const ProjectSizeAdjustmentsSection: React.FC<{
    scenario: ScenarioMetadata;
    availableProjects: (QueueProject | SpeculativeProject)[];
    projectIdToCluster: Record<string, LegacyClusterInfo>;
    /** Set false where a surrounding panel already offers the export. */
    showExport?: boolean;
}> = ({
    scenario,
    availableProjects,
    projectIdToCluster,
    showExport = true
}) => {
    const includesCrossClusterProjects = useIncludesCrossClusterQueueProjects();
    const config = useRegionConfig();

    const scenarioAssumptions = useMemo(
        () => getSizeAdjustments(scenario),
        [scenario]
    );

    const projectSizePreviews = useMemo(() => {
        return Object.keys(scenarioAssumptions)
            .map((projectId) => {
                const project = availableProjects.find(
                    (p) => p.projectId === projectId
                );
                if (
                    project &&
                    project.type === ProjectType.SPECULATIVE_PROJECT
                ) {
                    // We display speculative projects in a separate section, so we can ignore it
                    // for now
                    return null;
                }

                const percentageReduction = scenarioAssumptions[projectId];
                const { eris: erisReduction, nris: nrisReduction } =
                    percentageReduction;
                let reductionInfo = <>excluded</>;

                if (!project) {
                    // This could happen if the project is now withdrawn, or it no longer
                    // exists in the all_projects table for some reason
                    if (erisReduction !== 100 || nrisReduction !== 100) {
                        reductionInfo = (
                            <>
                                reduced by {erisReduction}% for{" "}
                                {config.sizeLabel.energyLabel} and{" "}
                                {nrisReduction}% for{" "}
                                {config.sizeLabel.capacityLabel}
                            </>
                        );
                    }
                    return (
                        <SectionCard
                            key={projectId}
                            padded={false}
                            className={css["scenario-assumptions-card"]}
                        >
                            <strong>{projectId}</strong> is {reductionInfo}
                        </SectionCard>
                    );
                }

                if (erisReduction !== 100 || nrisReduction !== 100) {
                    reductionInfo = (
                        <ProjectSizePreviewWrapper
                            erisReduction={erisReduction}
                            nrisReduction={nrisReduction}
                            project={project}
                            percentageReduction={percentageReduction}
                        />
                    );
                }

                // projectCluster can be missing if the project isn't in the
                // cluster mapping (e.g. cross-cluster gaps); fall back to no
                // label instead of dereferencing undefined.
                const projectCluster = projectIdToCluster[projectId];
                const cycleAndGroupLabel = projectCluster
                    ? getCycleAndGroupLabel(
                          projectCluster.region,
                          projectCluster.studyCycle,
                          projectCluster.studyGroup
                      )
                    : "";

                return (
                    <SectionCard
                        key={projectId}
                        padded={false}
                        className={css["scenario-assumptions-card"]}
                    >
                        <strong>{projectId}</strong>
                        {includesCrossClusterProjects && cycleAndGroupLabel
                            ? ` - ${cycleAndGroupLabel}`
                            : ""}{" "}
                        is {reductionInfo}{" "}
                    </SectionCard>
                );
            })
            .filter((element: JSX.Element | null) => element !== null);
    }, [
        scenarioAssumptions,
        availableProjects,
        projectIdToCluster,
        config,
        includesCrossClusterProjects
    ]);

    const numScenarioAssumptions = projectSizePreviews.length;

    return (
        <ScenarioAssumptionsSection
            section="projectSizeAdjustments"
            title={<>Project Size Adjustments ({numScenarioAssumptions})</>}
            rightElement={
                showExport ? (
                    <ExportAssumptionsButton
                        scenario={scenario}
                        className={css["scenario-assumptions-export-button"]}
                    />
                ) : undefined
            }
        >
            {numScenarioAssumptions > 0 ? (
                projectSizePreviews
            ) : (
                <SectionCard
                    padded={false}
                    className={`${css["scenario-assumptions-card"]} ${Classes.TEXT_MUTED}`}
                >
                    No adjustments have been configured for this scenario.
                </SectionCard>
            )}
        </ScenarioAssumptionsSection>
    );
};
